import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';

import {
    pageContext,
    elementRulesStateToProps,
    get,
    applyVerticalStyles,
    interpolateString
} from 'ctm-util-functions';

import { FieldRow } from '../field_row/field_row';
import { SimpleFlexbox } from '../../../flexbox/flexbox';
import { HelpIndicator } from '../../help_indicator/help_indicator';
import { Anchor } from '../../../anchor/anchor';
import { Accordion } from '../../../accordion/accordion';
import {
    isVisible,
    setVisibilityAction,
    shouldComponentUpdate as _shouldComponentUpdate
} from '../../utils';

import { getSelectedValues } from '../../../utils';

import styles from './field_set.css';

/**
 * _FieldSet component. This is a top level form element that
 * contains other form elements
 */
class _FieldSet extends Component {

    constructor(props) {
        super(props);
        this.id = props.schema.identifier;
        let stateSelector = 'schema.gridProps';
        this.styles = applyVerticalStyles(styles, 'field_set');
        const displayMode = get(props, 'extraProps.visualProps.displayMode');
        if (displayMode) {
            stateSelector = `${stateSelector}.${displayMode}`;
        } else {
            stateSelector = `${stateSelector}.rowVisualProps`;

            // we don't currently have a case were we define rowVisualProps_BREAKPOINT - do we want to remove the following

            // const breakpoint = pageContext.getProps().pageState.breakpoint;
            // const breakpointGridProps = get(this.props, `${stateSelector}_${breakpoint}`);
            // if (breakpointGridProps) {
            //     stateSelector = `${stateSelector}_${breakpoint}`;
            // }
        }
        this.rowVisualProps = get(this.props, stateSelector, { variant: 'field_row' });
        this.formElementFactory = pageContext.getProps().factories.formElement;

        this.formSchemaModel = pageContext.getProps().models.formSchemaModel;
    }

    /**
     * Determine if the component should re-render based on the nextProps
     * @param  {Object} nextProps  The incoming set of props
     * @param  {Object} nextState  The incoming state
     * @returns {Boolean}           true if the component should re-render
     */
    shouldComponentUpdate = (nextProps, nextState) => {
        return _shouldComponentUpdate(nextProps, nextState, this.props, this.state);
    };

    /**
     * Set the visibility state of the field set when it mounts
     */
    componentDidMount = () => {
        const { dispatch, schema } = this.props;
        if (get(this.props, 'rules.visibility')) {
            dispatch(setVisibilityAction(this.id, schema.stateKey, isVisible(this.props)));
        }
    };

    /**
     * Set the visibility state of the field set when it updates
     * @param {Object} prevProps - The previous set of props
     */
    componentDidUpdate = (prevProps) => {
        const { rules, dispatch, schema } = this.props;
        if (get(this.props, 'rules.visibility')) {
            if (rules.visibility.result !== prevProps.rules.visibility.result) {
                // extra sanity check - only change visibility if it has changed
                dispatch(setVisibilityAction(this.id, schema.stateKey, isVisible(this.props)));
            }
        }
    };

    /**
     * Get the title of the Field Set.
     * @param  {Object} schema - element schema.
     * @returns {Node|null} - h2 component with the title on it.
     */
    getTitle = (schema) => {
        const { title } = schema;
        const { selectedValues } = this.props;
        if (title) {
            const titleClassName = get(schema, 'title.variant', 'fieldset_title');
            const subTitleClassName = get(schema, 'title.subTitleVariant', 'fieldset_sub_title');
            let hr = null;
            if (title.horizontalRule) {
                hr = <hr className={this.styles.hr_small_padding} />;
            }

            let optional = null;
            if (title.optional) {
                optional = <span className={this.styles.optional_indicator}>OPTIONAL</span>;
            }
            const { anchor } = title;
            const anchorElement = anchor ? <Anchor {...anchor} className={styles[anchor.cssClass]}>{anchor.label}</Anchor> : null;
            const ParentComponent = title.inline ? SimpleFlexbox : Fragment;
            const parentProps = title.inline ? { className: styles.inline_header } : {};

            return (
                <ParentComponent {...parentProps}>
                    {optional}
                    <h2 className={this.styles[titleClassName]}>
                        {interpolateString(schema.title.label)(selectedValues)}
                    </h2>
                    <h4 className={this.styles[subTitleClassName]}>
                        {schema.title.subTitle}
                    </h4>
                    {anchorElement}
                    {hr}
                </ParentComponent>
            );
        }
        return null;
    };

    /**
     * Get the Label of the Field Set.
     * @param  {Object} schema - element schema.
     * @returns {Array|null} - The Label form component.
     */
    getLabel = (schema) => {
        const { selectedValues } = this.props;
        if (schema.label) {
            return this.formElementFactory.createLabel({
                schema,
                selectedValues
            });
        }
        return null;
    };

    /**
     * Get the text content of the Field Set.
     * @param  {Object} schema - element schema.
     * @returns {Node|null} - h3 component with the content on it.
     */
    getContent = (schema) => {
        let content = null;
        if (schema.content) {
            content = <h3 className={this.styles.form_sub_heading}>{schema.content}</h3>;
        }
        return content;
    };

    /**
     * Render the horizontal rule between fieldsets.
     * @param {Object} schema element schema
     * @param {String} placement Show it before or after the fieldset.
     * If you want it before AND after.. too bad (change the schema structure to before: true, after: true
     * @returns {*} A Horizontal rule
     */
    getHorizontalRule = (schema, placement = 'before') => {
        if (!schema.horizontalRule) {
            return null;
        }
        const { variant, position = 'before' } = schema.horizontalRule;

        // This may require random arrows ^ added to it.
        const horizontalRule = <hr className={this.styles[`hr_${variant}`]} />;
        return placement === position ? horizontalRule : null;
    };

    /**
     * Create the FieldRows for each element in the FieldSet's schema.
     * @returns {Array} - The array of FieldRows for this FieldSet.
     */
    createFieldRows = () => {
        const fieldRows = [];
        const { schema, extraProps } = this.props;
        const { identifier, layoutMode = 'column', elements = [] } = schema;
        const fieldRowStyle = layoutMode === 'multiColumns' ? { className: this.styles[`multiColumn_${schema.rowMode}`] } : null;

        for (const elementSchema of elements) {
            // @todo: figure out a better way of doing this. This is necessary for overrides (whiteLabel and competitions)
            let element = this.formSchemaModel.getElement('vertical.quote', elementSchema.identifier, elementSchema.identifierCategory);
            if (!element) {
                element = elementSchema;
            }
            const key = `${identifier}_${elementSchema.identifier}_fieldRow`;

            let FieldRowComponent = FieldRow;
            if (element.fieldRowType) {
                const customFieldRows = get(pageContext.getProps().verticalComponents, 'CustomFieldRows', {});
                // add the field set identifier so the fieldRow knows what the parent is
                element.fieldSetIdentifier = identifier;
                FieldRowComponent = customFieldRows[element.fieldRowType];
            }

            if (!FieldRowComponent) {
                throw Error(`A FieldRow with fieldRowType: ${element.fieldRowType} was defined, but this fieldRowType doesn't exist`);
            }

            fieldRows.push((
                <FieldRowComponent
                    {...fieldRowStyle}
                    schema={element}
                    key={key}
                    layoutMode={layoutMode}
                    extraProps={extraProps}
                />
            ));
        }
        return fieldRows;
    };

    /**
     * Get the children for the Field Set.
     * This wraps this.props.children in a FlexBox so are rendered in a horizontal row if layoutMode = 'row' is specified
     * in the schema, otherwise this.props.children is returned with no transformation and children are rendered in a
     * vertical column.
     * @param {Object} schema - the element schema
     * @returns {Node} - the fieldSet child components
     */
    getChildren = (schema) => {
        const layoutMode = schema.layoutMode || 'column';
        let children = this.createFieldRows();

        if (layoutMode === 'multiColumns') {
            const className = schema.rowMode === 'row_full_width' ? 'multiColumn_noWrap' : 'multiColumn';
            return (
                <div className={this.styles[className]}>
                    {children}
                </div>
            );
        }

        if (layoutMode === 'row') {
            children = (
                <SimpleFlexbox row>
                    <SimpleFlexbox {...this.rowVisualProps}>
                        {children}
                    </SimpleFlexbox>
                </SimpleFlexbox>
            );
        }

        return children;
    };

    /**
     * Render Two Flexbox components with a HelpIndicator and Text Component if it has the props to do so
     * @param {Object} schema - schema Object
     * @param {Object} schema.helpText - helpText content
     * @param {String} schema.identifier - elements identifier
     * @returns {Node|Null} A component or null
     */
    getHelpText = ({ helpText, identifier }) => {
        if (!helpText) {
            return null;
        }
        const { label, content, title } = helpText;
        const helpTextProps = {
            schema: { identifier: `${identifier}_tooltip` },
            contentProps: { content, title }
        };

        return (
            <SimpleFlexbox display="flex">
                <SimpleFlexbox margin="left_auto">
                    <HelpIndicator {...helpTextProps} />
                    {label && <span>{label}</span> }
                </SimpleFlexbox>
            </SimpleFlexbox>
        );
    };

    /**
     * Render the FieldSet contents and handles whether to display fieldset in accordion component
     * @param {Object} schema - the element schema
     * @returns {Node} contents for fieldset wrapped in Fragment or Accordion
     */
    getFieldSetContents() {
        const { schema } = this.props;
        const children = [
            this.getLabel(schema),
            this.getContent(schema),
            this.getChildren(schema),
            this.getHelpText(schema)
        ];

        let parentProps = {};
        let parentComponent = Fragment;

        if (schema.accordion) {
            parentComponent = Accordion;
            parentProps = { ...schema.accordion, contentProps: schema.title };
        } else {
            children.unshift(this.getTitle(schema));
        }
        return React.createElement(parentComponent, parentProps, ...children);
    }

    /**
     * Render the FieldSet if it is visible.
     * @returns {Node|null} The FieldSet and all child elements if the Form Group
     * is visible, null otherwise.
     */
    render = () => {
        const { schema, className } = this.props;
        if (!isVisible(this.props)) {
            return null;
        }

        const finalClassName = className || this.styles[schema.cssClass] || this.styles.root;

        return (
            <div className={finalClassName} key={schema.identifier} data-id={schema.identifier}>
                {this.getHorizontalRule(schema, 'before')}
                {this.getFieldSetContents(schema)}
                {this.getHorizontalRule(schema, 'after')}
            </div>
        );
    };

}

_FieldSet.propTypes = {
    schema: PropTypes.shape({
        helpText: PropTypes.shape({
            label: PropTypes.string,
            content: PropTypes.string.isRequired
        }),
        elType: PropTypes.oneOf(['fieldSet', 'multiFieldAddress']).isRequired,
        identifier: PropTypes.string.isRequired,
        accordion: PropTypes.object,
        field: PropTypes.object,
        title: PropTypes.shape({
            label: PropTypes.string,
            variant: PropTypes.string,
            subTitle: PropTypes.string,
            subTitleVariant: PropTypes.string
        }),
        horizontalRule: PropTypes.shape({
            variant: PropTypes.oneOf(['default']),
            position: PropTypes.oneOf(['before', 'after'])
        }),
        layoutMode: PropTypes.oneOf(['row', 'multiColumns', 'column']),
        rowMode: PropTypes.oneOf(['row', 'row_full_width', 'address']),
        elements: PropTypes.array,
        stateKey: PropTypes.string,
        cssClass: PropTypes.string
    }).isRequired,
    className: PropTypes.string,
    dispatch: PropTypes.func.isRequired,
    rules: PropTypes.object,
    selectedValues: PropTypes.object,
    breakpoint: PropTypes.string,
    extraProps: PropTypes.object
};

/**
 * Add the required state attributes to props - rules
 * @param {Object} state - the current application state
 * @param {Object} props - the incoming set of props
 * @returns {Object} - The state attrs to add to props.
 */
const mapStateToProps = (state, props) => {
    return {
        rules: elementRulesStateToProps()(state, props).rules,
        selectedValues: getSelectedValues(state, props)
    };
};

const FieldSet = connect(mapStateToProps)(_FieldSet);
export { _FieldSet, FieldSet };
